"use client";

import React from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { HeroUIProvider } from "@heroui/system";
import { useRouter } from "next/navigation";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { PrivyProvider } from "@privy-io/react-auth";

import { queryClient } from "@/lib/queryClient";
import { AuthProvider } from "@/contexts/auth-context";
import { MasqueradeProvider } from "@/contexts/masquerade-context";
import { ModalProvider } from "@/contexts/modal-context";
import { Toaster } from "sonner";

export interface ProvidersProps {
  children: React.ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  const router = useRouter();

  return (
    <PrivyProvider
      appId={process.env.NEXT_PUBLIC_PRIVY_APP_ID || ""}
      config={{
        loginMethods: ["email", "wallet"],
        appearance: {
          theme: "light",
          accentColor: "#EA5555",
        },
        embeddedWallets: {
          ethereum: {
            createOnLogin: "users-without-wallets",
          },
        },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <MasqueradeProvider>
          <AuthProvider>
            <ModalProvider>
              <HeroUIProvider navigate={(path) => router.push(path)}>
                <NextThemesProvider attribute="class" defaultTheme="light">
                  <Toaster />
                  {children}
                  <ReactQueryDevtools
                    initialIsOpen={false}
                    buttonPosition="bottom-right"
                  />
                </NextThemesProvider>
              </HeroUIProvider>
            </ModalProvider>
          </AuthProvider>
        </MasqueradeProvider>
      </QueryClientProvider>
    </PrivyProvider>
  );
}
